/** Карточка поставки (боковая панель, `card=supply:<номер заявки>`): шапка (юрлицо, склад →
 *  кластер, слот, статус, виды проблем) → KPI → начисления по статьям → состав и расхождения →
 *  артикулы поставки. Графика нет: начисления по поставке разовые.
 *
 *  Начисления (KPI «Начисления за всё время», «Начисления по статьям») — за ВСЁ время данных
 *  витрины (`mart.date_from…date_to` из `/meta`), без сравнения и без маржи: они разовые и
 *  приходят месяцами позже отгрузки, выручки у поставки нет — Δ к прошлому периоду и «маржа» шум.
 *
 *  Номер заявки = `obj_id` витрины у `obj_kind='supply'` = `order_number` диагноза (так их строит
 *  `ozon-fin build-mart`: unit_number начисления ищется среди `ozon_fin_supplies.order_number`),
 *  поэтому одна строка-ключ годится для всех источников:
 *  - деталь — `GET /supplies/{n}` (диагноз, акт, начисления по месяцам за всё время);
 *  - начисления за всё время — `/summary?scope=obj:supply:<n>` на диапазоне витрины (со scope по
 *    объекту бэкенд не ограничивает период 400 днями);
 *  - артикулы (за всё время, без сравнения) — `POST /tree` `levels=['obj','article']`, `path=[obj supply:<n>]`.
 *  Юрлицо и общие расходы — фильтры страницы; период у всех блоков — вся витрина (`/meta`).
 *  Каждый блок грузится сам: свой скелетон, своя ошибка текстом. */
import React, { useMemo } from "react";

import type { OzFilters, OzSupplyDetail, TreeRequest } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { shown } from "./calc";
import { Block, Empty, More, Skeleton } from "./cardBlocks";
import { fmtDate, rub } from "./format";
import { KpiGrid, PnlTable } from "./pnl";
import { useMeta, useSummary, useSupplyDetail, useTreePage } from "./queries";
import { CardButton, ErrorBox } from "./shared";
import type { CardRef } from "./state";
import { SupplyAccrualsSection, SupplyActSection, SupplyDiagSection } from "./SupplyDetail";
import { IssueChips } from "./SupplyIssues";
import { supplyChargesKpi, supplyDiffKpi, supplyUnitsKpi, supplyXdKpi } from "./supplyKpi";
import { tdNum, thClass } from "./ui";

/** Артикулов в блоке «Артикулы поставки» (дальше — «и ещё N»). */
const ARTICLES_TOP = 30;

export function SupplyCard({
  orderNumber,
  filters,
  warning,
  titleId,
  onOpenCard,
}: {
  orderNumber: string;
  filters: OzFilters;
  warning: string | null;
  /** id заголовка — на него ссылается aria-labelledby панели */
  titleId: string;
  /** переход к карточке артикула поставки внутри панели */
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const detail = useSupplyDetail(orderNumber, filters.legal, filters.alloc);
  // заявки нет ни в диагнозе, ни в начислениях (за всё время, у юрлица фильтра) — только шапка с
  // ошибкой: блоки показали бы «честные нули» несуществующей поставки
  const notFound =
    (detail.error as { response?: { status?: number } } | null)?.response?.status === 404;
  return (
    <div className="space-y-5">
      <Header q={detail} orderNumber={orderNumber} titleId={titleId} />
      {!notFound && (
        <SupplyBlocks
          orderNumber={orderNumber}
          filters={filters}
          warning={warning}
          detail={detail}
          onOpenCard={onOpenCard}
        />
      )}
    </div>
  );
}

type DetailQuery = ReturnType<typeof useSupplyDetail>;

/** Уникальные непустые значения по порядку появления. */
function uniq(values: (string | null)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v))];
}

function Header({
  q,
  orderNumber,
  titleId,
}: {
  q: DetailQuery;
  orderNumber: string;
  titleId: string;
}): React.ReactElement {
  const d = q.data;
  const supplies = d?.supplies ?? [];
  const issues = supplies.flatMap((s) => s.issues ?? []);
  const slots = uniq(supplies.map((s) => s.slot_date)).sort();
  const route = uniq(
    supplies.map((s) =>
      [s.dropoff, s.cluster && `→ ${s.cluster}`].filter(Boolean).join(" ") || null,
    ),
  );
  const states = uniq(supplies.map((s) => s.state));
  return (
    <div className="space-y-1" data-testid="supply-header">
      <h2 id={titleId} className="text-xl font-semibold leading-tight tabular-nums">
        Поставка {orderNumber}
      </h2>
      {q.isLoading && <div className="h-4 w-64 animate-pulse rounded bg-muted" />}
      {d && (
        <>
          <div className="text-sm">{d.legal_names.join(", ") || "—"}</div>
          {supplies.length > 0 ? (
            <div className="space-y-0.5 text-xs text-muted-foreground">
              <div>
                {supplies.length > 1 && `${supplies.length} поставки в заявке · `}
                склад: {route.join("; ") || "—"}
              </div>
              <div>
                слот: {slots.length ? slots.map(fmtDate).join(", ") : "—"} · статус:{" "}
                {states.join(", ") || "—"}
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              Диагноза нет: заявки нет в ozon_fin_supply_diag — есть только начисления.
            </div>
          )}
          {supplies.length > 0 && (
            <div>
              <IssueChips issues={issues} />
            </div>
          )}
        </>
      )}
      {q.isError && <ErrorBox error={q.error} what="поставку" />}
    </div>
  );
}

function SupplyBlocks({
  orderNumber,
  filters,
  warning,
  detail,
  onOpenCard,
}: {
  orderNumber: string;
  filters: OzFilters;
  warning: string | null;
  detail: DetailQuery;
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const scope = useMemo(() => [`obj:supply:${orderNumber}`], [orderNumber]);
  // карточка поставки — только у Ozon (mp.ts)
  const meta = useMeta("ozon");
  const range = meta.data ? { from: meta.data.mart.date_from, to: meta.data.mart.date_to } : null;
  // начисления — за всё время данных витрины, без сравнения (юрлицо и общие — как на странице);
  // пока /meta не пришла, сводку не запрашиваем
  const allTime: OzFilters = {
    ...filters,
    date_from: range?.from ?? filters.date_from,
    date_to: range?.to ?? filters.date_to,
    cmp_from: null,
    cmp_to: null,
  };
  const sum = useSummary("ozon", allTime, scope, range !== null);
  const d = detail.data;
  // начисления — из сводки со scope, штуки и расхождения — из детали (ошибка детали — в шапке)
  const kpis = [
    ...(sum.data && range ? [supplyChargesKpi(sum.data, range)] : []),
    ...(d ? [supplyUnitsKpi(d), supplyXdKpi(d), supplyDiffKpi(d)] : []),
  ];
  const loading = meta.isLoading || sum.isLoading;
  return (
    <div className="space-y-5">
      <Block title="Показатели">
        {(loading || detail.isLoading) && <Skeleton rows={1} />}
        {meta.isError && <ErrorBox error={meta.error} what="диапазон данных витрины" />}
        {sum.isError && <ErrorBox error={sum.error} what="начисления поставки" />}
        {kpis.length > 0 && <KpiGrid kpis={kpis} warning={warning} compact />}
      </Block>

      <Block title="Начисления по статьям за всё время">
        {loading && <Skeleton rows={3} />}
        {sum.isError && <ErrorBox error={sum.error} what="начисления по статьям" />}
        {sum.data && (
          <PnlTable
            summary={sum.data}
            filters={allTime}
            warning={warning}
            charts={false}
            compact
            margin={false}
          />
        )}
      </Block>

      {!detail.isError && (
        <Block title="Состав и расхождения">
          {detail.isLoading && <Skeleton rows={3} />}
          {d && <Composition detail={d} />}
        </Block>
      )}

      <ArticlesBlock
        orderNumber={orderNumber}
        filters={allTime}
        range={range}
        onOpenCard={onOpenCard}
      />
    </div>
  );
}

function Composition({ detail }: { detail: OzSupplyDetail }): React.ReactElement {
  return (
    <div className="space-y-4 rounded-md border bg-card p-3">
      <SupplyDiagSection supplies={detail.supplies} />
      <SupplyActSection items={detail.act_items} compact />
      <SupplyAccrualsSection detail={detail} />
    </div>
  );
}

/** Артикулы, на которые витрина разложила начисления поставки (по составу заявки), дороже первыми. */
function ArticlesBlock({
  orderNumber,
  filters,
  range,
  onOpenCard,
}: {
  orderNumber: string;
  /** фильтры «за всё время»: диапазон витрины из /meta, без сравнения */
  filters: OzFilters;
  /** диапазон витрины; null — /meta ещё не пришла, дерево не запрашиваем */
  range: { from: string; to: string } | null;
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const req: TreeRequest = {
    ...filters,
    cmp_from: null,
    cmp_to: null,
    levels: ["obj", "article"],
    path: [{ dim: "obj", key: `supply:${orderNumber}` }],
    search: null,
    limit: ARTICLES_TOP,
    offset: 0,
    sort: "cost",
    obj_kind: null,
  };
  const q = useTreePage("ozon", req, range !== null);
  return (
    <Block title="Артикулы поставки за всё время">
      {q.isLoading && <Skeleton rows={3} />}
      {q.isError && <ErrorBox error={q.error} what="артикулы поставки" />}
      {q.data && q.data.rows.length === 0 && <Empty />}
      {q.data && q.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full table-fixed text-xs" data-testid="supply-articles-table">
            <colgroup>
              <col />
              <col style={{ width: "16ch" }} />
            </colgroup>
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>Артикул</th>
                <th className={thClass}>Начисления</th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-2 py-1">
                    <div className="flex items-start gap-1">
                      <span className="font-medium">{r.label}</span>
                      {r.key !== "" && (
                        <CardButton
                          label={`Карточка артикула ${r.key}`}
                          title="Карточка артикула"
                          onOpen={() => onOpenCard({ kind: "article", key: r.key })}
                        />
                      )}
                    </div>
                    {r.sublabel && (
                      <div className="truncate text-muted-foreground" title={r.sublabel}>
                        {r.sublabel}
                      </div>
                    )}
                  </td>
                  <td className={tdNum}>{rub(shown(r.cur.rev + r.cur.cost, "expense"))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <More data={q.data} noun="артикулов" />
        </div>
      )}
    </Block>
  );
}
